import { CommonModule, AsyncPipe } from '@angular/common';
import { HttpEventType, HttpStatusCode, HttpErrorResponse, provideHttpClient, withInterceptors } from '@angular/common/http';
import * as i0 from '@angular/core';
import { inject, Injectable, InjectionToken, EventEmitter, ChangeDetectionStrategy, Component, Output, NgModule } from '@angular/core';
import { Subject, ReplaySubject, Subscription, combineLatest, takeUntil, throwError, Observable, of } from 'rxjs';
import { jwtDecode, CookieService, unsubscribe } from '@tc/foundation/utilities';
import { EnvironmentService } from '@tc/foundation';
import { first, switchMap, map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { StlrDialogRef } from '@tc/interstellar-dialog';
import * as i1 from '@tc/interstellar-icon';
import { StlrIconModule } from '@tc/interstellar-icon';
import * as i2 from '@tc/interstellar-button';
import { StlrButtonModule } from '@tc/interstellar-button';

class AuthenticationToken {
    constructor(token, fromStorage) {
        this.exists = true;
        if (token == null || token === '') {
            throw new Error('AuthenticationToken can not contain a null or empty token; use EmptyToken instead');
        }
        this._token = token;
        this._fromStorage = fromStorage;
    }
    get token() {
        return this._token;
    }
    get fromStorage() {
        return this._fromStorage;
    }
    get isExpired() {
        const expireTime = this.tokenExpirationTime;
        if (expireTime == null) {
            return true;
        }
        return new Date() > expireTime;
    }
    /**
     * The UTC time the token expires if it exists
     * @returns the the token's expiration time - null if there is no expiration time
     */
    get tokenExpirationTime() {
        const decoded = jwtDecode(this.token);
        if (decoded?.exp == null) {
            return null;
        }
        const time = new Date(0);
        time.setUTCSeconds(decoded.exp);
        return time;
    }
    get isValid() {
        return this.exists && !this.isExpired;
    }
    /**
     * The time of the initial authentication request, expressed as UNIX time which
     * is the number of seconds since Epoch. This claim will remain the same even
     * when the token has been re-issued through the use of a Refresh Token.
     */
    get tokenValidFrom() {
        const decoded = jwtDecode(this.token);
        const time = new Date(0);
        time.setUTCSeconds(decoded.nbf);
        return time;
    }
    /**
     * The unique ID of the refresh token returned along with this access token
     * when the offline_access scope was requested. This unique ID is the persistent
     * identifier for the refresh token, and will not change even when using one-time
     * use refresh tokens. This value may optionally be used to revoke the token using
     * the Refresh Token API.
     */
    get sessionId() {
        const decoded = jwtDecode(this.token);
        const sessionId = decoded.sid;
        return sessionId;
    }
}

function generateApiUrl(apiUrl, endpoint, returnUrl, additionalQueryStringParams) {
    returnUrl = encodeURIComponent(returnUrl ?? window.location.origin);
    let url = `${apiUrl}${endpoint}${returnUrl ? `?returnUrl=${returnUrl}` : ''}`;
    if (additionalQueryStringParams) {
        url = `${url}&${Object.keys(additionalQueryStringParams)
            .map((key) => `${key}=${additionalQueryStringParams[key]}`)
            .join('&')}`;
    }
    return url;
}

class EmptyToken {
    constructor() {
        this.token = null;
        this.exists = false;
        this.tokenExpirationTime = null;
        this.isExpired = false;
        this.fromStorage = false;
        this.isValid = false;
        this.tokenValidFrom = null;
        this.sessionId = null;
    }
}

const AUTH_IFRAME_IDENTIFIER = 'authRenewIframe';
const AUTH_EVENT_IDENTIFIER = 'auth-refresh-message';
const REFRESH_TOKEN_PAGE_LOCATION = '/assets/refresh-token.html';
/**
 * Manages OAuth authentication tokens including storage, refresh, and lifecycle.
 *
 * @remarks
 * This service handles:
 * - Token storage in cookies
 * - Automatic token refresh via hidden iframe
 * - Token expiration detection
 * - Token validation
 *
 * The service is provided in root and automatically integrates tokens on initialization.
 *
 * @example
 * ```typescript
 * constructor(private tokenService: TokenService) {
 *   // Subscribe to token changes
 *   this.tokenService.token$.subscribe(token => {
 *     if (token.isValid) {
 *       console.log('User authenticated:', token.subject);
 *     }
 *   });
 *
 *   // Check if token refresh is in progress
 *   this.tokenService.isRefreshingToken$.subscribe(isRefreshing => {
 *     this.showLoadingSpinner = isRefreshing;
 *   });
 * }
 * ```
 */
class TokenService {
    constructor() {
        /**
         * Observable that emits when token refresh fails.
         *
         * @remarks
         * Emits when the refresh token is invalid or refresh operation fails.
         * Typically triggers a logout and redirect to login.
         */
        this.refreshTokenInvalidSubject = new Subject();
        this.refreshTokenInvalid$ = this.refreshTokenInvalidSubject.asObservable();
        this.boundAuthRefreshEvent = null;
        this.isIFrameInitialized = false;
        this.sessionIFrame = null;
        /**
         * Observable that emits the current authentication token.
         *
         * @remarks
         * Emits whenever the token changes (login, refresh, logout).
         * Returns an `EmptyToken` when no valid token exists.
         *
         * @example
         * ```typescript
         * this.tokenService.token$.subscribe(token => {
         *   if (token.exists && token.isValid) {
         *     // Access token claims
         *     console.log('Account ID:', token.accountId);
         *     console.log('Expires:', token.expirationDate);
         *   }
         * });
         * ```
         */
        this.tokenSubject = new ReplaySubject(1);
        this.token$ = this.tokenSubject.asObservable();
        /**
         * Observable that emits the current token refresh state.
         *
         * @remarks
         * Emits `true` when a token refresh is in progress, `false` otherwise.
         * Use this to show loading indicators during token refresh operations.
         *
         * @example
         * ```typescript
         * this.tokenService.isRefreshingToken$.subscribe(isRefreshing => {
         *   this.loadingIndicator = isRefreshing;
         * });
         * ```
         */
        this.isRefreshingTokenSubject = new ReplaySubject(1);
        this.isRefreshingToken$ = this.isRefreshingTokenSubject.asObservable();
        this.cookieService = inject(CookieService);
        this.envService = inject(EnvironmentService);
        this.integrateToken();
        this.initAuthIFrame();
    }
    /**
     * Refreshes the authentication token using a hidden iframe.
     *
     * @remarks
     * This method:
     * 1. Sets `isRefreshingToken$` to `true`
     * 2. Loads the refresh token endpoint in a hidden iframe
     * 3. iframe receives updated token from server
     * 4. iframe dispatches event to parent window
     * 5. Token is updated and `isRefreshingToken$` set to `false`
     *
     * If refresh fails, `refreshTokenInvalid$` emits and user is logged out.
     *
     * @example
     * ```typescript
     * // Manual token refresh (usually automatic)
     * this.tokenService.refreshToken();
     *
     * // Monitor refresh state
     * this.tokenService.isRefreshingToken$.subscribe(isRefreshing => {
     *   console.log('Token refresh in progress:', isRefreshing);
     * });
     * ```
     */
    refreshToken() {
        this.isRefreshingTokenSubject.next(true);
        if (!this.isIFrameInitialized) {
            this.initAuthIFrame();
        }
        this.sessionIFrame = this.getExistingAuthIFrame(AUTH_IFRAME_IDENTIFIER);
        this.envService.configuration$
            .pipe(first())
            .subscribe((configuration) => {
            const source = generateApiUrl(configuration.apiUrl, configuration.refreshTokenEndpoint, `${window.location.origin}${REFRESH_TOKEN_PAGE_LOCATION}`);
            this.sessionIFrame.src = source;
        });
    }
    /**
     * Clears the current authentication token from cookie storage.
     *
     * @remarks
     * This method:
     * - Deletes the token cookie from browser storage
     * - Emits an `EmptyToken` to `token$` observable
     * - Sets `isRefreshingToken$` to `false`
     *
     * Typically called during logout or when token refresh fails.
     *
     * @example
     * ```typescript
     * // Clear token on logout
     * this.tokenService.clearToken();
     *
     * // Subscribe to token changes
     * this.tokenService.token$.subscribe(token => {
     *   if (!token.exists) {
     *     console.log('Token cleared - user logged out');
     *   }
     * });
     * ```
     */
    clearToken() {
        this.envService.configuration$.pipe(first()).subscribe((config) => {
            const tokenName = this.getTokenName(config);
            this.cookieService.delete(tokenName, '/', config.tokenDomain);
            this.tokenSubject.next(new EmptyToken());
            this.isRefreshingTokenSubject.next(false);
        });
    }
    setToken(token) {
        this.tokenSubject.next(new AuthenticationToken(token, false));
        this.isRefreshingTokenSubject.next(false);
    }
    authRefreshEventHandler() {
        this.envService.configuration$.pipe(first()).subscribe((config) => {
            const tokenName = this.getTokenName(config);
            const storedToken = this.cookieService.get(tokenName);
            if (storedToken) {
                this.setToken(storedToken);
            }
            else {
                this.clearToken();
                this.refreshTokenInvalidSubject.next();
            }
        });
    }
    getExistingAuthIFrame(identifier) {
        const iFrameOnParent = window.parent.document.getElementById(identifier);
        if (iFrameOnParent) {
            return iFrameOnParent;
        }
        return window.document.getElementById(identifier);
    }
    addAuthIFrameToWindowBody() {
        const sessionIFrame = window.document.createElement('iframe');
        sessionIFrame.id = AUTH_IFRAME_IDENTIFIER;
        sessionIFrame.style.display = 'none';
        window.document.body.appendChild(sessionIFrame);
        return sessionIFrame;
    }
    initAuthIFrame() {
        const existingIFrame = this.getExistingAuthIFrame(AUTH_IFRAME_IDENTIFIER);
        if (!existingIFrame) {
            this.addAuthIFrameToWindowBody();
        }
        this.isIFrameInitialized = true;
        this.boundAuthRefreshEvent = this.authRefreshEventHandler.bind(this);
        window.addEventListener(AUTH_EVENT_IDENTIFIER, this.boundAuthRefreshEvent, false);
    }
    /**
     * This only needs to be run when the app is initializing.
     * Otherwise, the token is processed in the background through the IFrame (auth-refresh.html).
     */
    integrateToken() {
        this.isRefreshingTokenSubject.next(true);
        this.envService.configuration$.pipe(first()).subscribe((config) => {
            const tokenName = this.getTokenName(config);
            const storedToken = this.cookieService.get(tokenName);
            if (!storedToken) {
                this.clearToken();
            }
            else {
                const authToken = new AuthenticationToken(storedToken, true);
                if (authToken.isExpired) {
                    this.refreshToken();
                }
                else {
                    this.setToken(storedToken);
                }
            }
        });
    }
    getTokenName(config) {
        return !config.production
            ? `${config.accessTokenName || config.applicationName}`
            : `${config.accessTokenName || config.applicationName}_${config.configurationName}`;
    }
    static { this.ɵfac = function TokenService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TokenService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TokenService, factory: TokenService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TokenService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

const SESSION_DIALOG_TOKEN$1 = new InjectionToken('sessionDialog');

class AuthenticationService {
    constructor() {
        this.envService = inject(EnvironmentService);
        this.tokenService = inject(TokenService);
        this.tokenSubscription = new Subscription();
        this.sessionTimeoutTimerId = null;
        this.sessionTimeoutIntervalId = null;
        this.isVerifyingSessionSubject = new ReplaySubject(1);
        this.isVerifyingSession$ = this.isVerifyingSessionSubject.asObservable();
        this.isAuthenticatedSubject = new ReplaySubject(1);
        this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
        this.logoutStartedSubject = new ReplaySubject(1);
        this.logoutStarted$ = this.logoutStartedSubject.asObservable();
        this.sessionTimeoutTimerSubject = new ReplaySubject(1);
        this.sessionTimeoutTimer$ = this.sessionTimeoutTimerSubject.asObservable();
        this.verifyingSubscription = new Subscription();
        this.destroy$ = new Subject();
        this.closeSubscription = new Subscription();
        this.sessionDialogDelegate = inject(SESSION_DIALOG_TOKEN$1);
        this.sessionDialog = undefined;
        this.initialize();
    }
    ngOnDestroy() {
        unsubscribe(...[
            this.closeSubscription,
            this.verifyingSubscription,
            this.tokenSubscription,
        ]);
        if (!this.destroy$.closed) {
            this.destroy$.next(true);
            this.destroy$.complete();
        }
    }
    login(destinationUrl) {
        this.envService.configuration$
            .pipe(first())
            .subscribe((configuration) => {
            const apiUrl = configuration.authorizeApiUrl == null
                ? configuration.apiUrl
                : configuration.authorizeApiUrl;
            const url = generateApiUrl(apiUrl, configuration.authorizeEndpoint, destinationUrl ? destinationUrl : window.location.href);
            window.open(url, '_self', 'toolbar=0,location=0,menubar=0');
        });
    }
    logout(destinationUrl) {
        this.tokenService.clearToken();
        this.envService.configuration$
            .pipe(first())
            .subscribe((configuration) => {
            const apiUrl = configuration.authorizeApiUrl == null
                ? configuration.apiUrl
                : configuration.authorizeApiUrl;
            const url = generateApiUrl(apiUrl, configuration.logOutEndpoint, destinationUrl);
            window.open(url, '_self', 'toolbar=0,location=0,menubar=0');
            this.logoutStartedSubject.next(true);
        });
    }
    continueSession() {
        if (this.sessionTimeoutTimerId != null) {
            clearTimeout(this.sessionTimeoutTimerId);
            this.sessionTimeoutTimerId = null;
        }
        if (this.sessionTimeoutIntervalId != null) {
            clearInterval(this.sessionTimeoutIntervalId);
            this.sessionTimeoutIntervalId = null;
        }
        this.tokenService.refreshToken();
        this.isVerifyingSessionSubject.next(false);
    }
    initialize() {
        this.tokenSubscription = combineLatest([
            this.envService.configuration$.pipe(first()),
            this.tokenService.token$,
        ]).subscribe(([configuration, token]) => {
            if (token.isValid) {
                this.setSessionTimeoutTimer(token, configuration);
                this.isAuthenticatedSubject.next(true);
            }
            else {
                this.isAuthenticatedSubject.next(false);
            }
            this.sessionTimeoutTimerSubject.next(0);
            this.isVerifyingSessionSubject.next(false);
        });
        this.verifyingSubscription = this.isVerifyingSession$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isVerifying) => {
            if (isVerifying) {
                this.sessionDialog = this.sessionDialogDelegate();
                const component = this.sessionDialog.componentInstance;
                component.timer$ = this.sessionTimeoutTimer$;
                component.logout
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                    this.tokenService.clearToken();
                    this.logout();
                });
                component.continue
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => this.continueSession());
                this.closeSubscription = combineLatest([
                    this.sessionDialog.afterClosed(),
                    this.sessionTimeoutTimer$,
                ])
                    .pipe(first())
                    .subscribe(([, timer]) => {
                    if (timer > 0)
                        this.continueSession();
                    else {
                        this.tokenService.clearToken();
                        this.logout();
                    }
                });
            }
            else if (this.sessionDialog) {
                this.sessionDialog.close();
            }
        });
        this.logoutStartedSubject.next(false);
        this.tokenService.refreshTokenInvalid$
            .pipe(first())
            .subscribe(() => this.logout(window.location.href));
    }
    setSessionTimeoutTimer(token, configuration) {
        if (this.sessionTimeoutTimerId != null) {
            clearTimeout(this.sessionTimeoutTimerId);
        }
        const timeout = configuration.refreshTokenLifeTime -
            (Date.now() - token.tokenValidFrom.valueOf()) -
            configuration.verifySessionActiveMs;
        this.sessionTimeoutTimerId = setTimeout(() => {
            this.startSessionConfirmation(configuration.verifySessionActiveMs);
        }, timeout);
    }
    startSessionConfirmation(timeout) {
        this.sessionTimeoutTimerSubject.next(timeout);
        timeout -= 1000;
        this.sessionTimeoutIntervalId = setInterval(() => {
            this.sessionTimeoutTimerSubject.next((timeout -= 1000));
        }, 1000);
        this.isVerifyingSessionSubject.next(true);
    }
    static { this.ɵfac = function AuthenticationService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AuthenticationService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthenticationService, factory: AuthenticationService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

class AuthenticationInterceptor {
    constructor() {
        this.tokenService = inject(TokenService);
        this.authService = inject(AuthenticationService);
        this.configurationService = inject(EnvironmentService);
        this.router = inject(Router);
        this.AUTH_HEADER = 'Authorization';
    }
    intercept(request, next) {
        return interceptRequest(request, next.handle, {
            authService: this.authService,
            configurationService: this.configurationService,
            tokenService: this.tokenService,
            router: this.router
        });
    }
    static { this.ɵfac = function AuthenticationInterceptor_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AuthenticationInterceptor)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthenticationInterceptor, factory: AuthenticationInterceptor.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationInterceptor, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();
function authInterceptor() {
    return (req, next) => {
        return interceptRequest(req, next, {
            authService: inject(AuthenticationService),
            configurationService: inject(EnvironmentService),
            tokenService: inject(TokenService),
            router: inject(Router)
        });
    };
}
function interceptRequest(request, next, deps) {
    return combineLatest([
        deps.authService.logoutStarted$,
        deps.configurationService.configuration$,
        deps.authService.isVerifyingSession$,
        deps.authService.sessionTimeoutTimer$,
    ]).pipe(first(), switchMap(([, configuration, verifying, sessionTimer]) => {
        //if the url is whitelisted or if the session is currently timed out (we don't want to redirect to the login if the session window is up)
        if (isUrlWhiteListed(request.url, configuration) ||
            (verifying && sessionTimer === 0)) {
            return next(request);
        }
        return handleRequest(request, next, deps).pipe(map((httpEvent) => {
            if (httpEvent.type === HttpEventType.Response) {
                const body = httpEvent.body;
                if (body?.statusCode ===
                    HttpStatusCode.BadRequest &&
                    body.fieldErrors?.request?.includes('InvalidAccountAccess')) {
                    if (configuration.accessDeniedUrl) {
                        if (configuration.accessDeniedUrl.startsWith('http')) {
                            window.location.href =
                                configuration.accessDeniedUrl;
                        }
                        else {
                            deps.router.navigate([
                                configuration.accessDeniedUrl,
                            ]);
                        }
                    }
                    throw new Error('InvalidAccountAccess');
                }
            }
            return httpEvent;
        }));
    }));
}
function handleRequest(request, next, deps) {
    return deps.tokenService.token$.pipe(first(), switchMap((authToken) => {
        if (!authToken.exists) {
            return next(request);
        }
        if (authToken.isExpired) {
            return refreshToken(request, next, deps);
        }
        return next(addAuthToken(request, authToken)).pipe(map((response) => {
            if (response.type === HttpEventType.Response) {
                const body = response.body;
                if (body?.statusCode ===
                    HttpStatusCode.Unauthorized ||
                    body?.statusCode === HttpStatusCode.Forbidden) {
                    throw new HttpErrorResponse({
                        error: body?.error,
                        headers: response.headers,
                        status: body?.statusCode.valueOf(),
                    });
                }
            }
            return response;
        }), catchError((error) => {
            // We should never see this. This is here to handle the off chance the token
            // is valid when this is called but by the time it hits the api it has expired.
            if (error.status === HttpStatusCode.Unauthorized ||
                error.status === HttpStatusCode.Forbidden) {
                return refreshToken(request, next, deps);
            }
            return throwError(() => error);
        }));
    }));
}
function refreshToken(request, next, deps) {
    deps.tokenService.refreshToken();
    return waitForTokenRefresh(request, next, deps);
}
function waitForTokenRefresh(request, next, deps) {
    return deps.tokenService.isRefreshingToken$.pipe(first((isRefreshing) => !isRefreshing), switchMap(() => deps.tokenService.token$.pipe(first())), switchMap((token) => next(addAuthToken(request, token))));
}
function addAuthToken(request, authToken) {
    if (authToken.exists) {
        const authRequest = request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + authToken.token),
        });
        return authRequest;
    }
    return request;
}
function isUrlWhiteListed(requestUrl, configuration) {
    let isWhiteListed = false;
    if (configuration.whiteList == null) {
        return isWhiteListed;
    }
    configuration.whiteList.forEach((urlPart) => {
        if (requestUrl.startsWith(urlPart)) {
            isWhiteListed = true;
        }
    });
    return isWhiteListed;
}

function ConfirmSessionComponent_Conditional_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h3", 10);
    i0.ɵɵtext(1, " Are you still there? ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵstyleProp("margin", "0px");
} }
function ConfirmSessionComponent_Conditional_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h3", 11);
    i0.ɵɵtext(1, " You have been logged out ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵstyleProp("margin", "0px");
} }
function ConfirmSessionComponent_Conditional_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵtext(1, " Would you like to stay logged in? ");
    i0.ɵɵelementEnd();
} }
function ConfirmSessionComponent_Conditional_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7);
    i0.ɵɵtext(1, " You will need to log in to continue. ");
    i0.ɵɵelementEnd();
} }
function ConfirmSessionComponent_Conditional_12_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 12);
    i0.ɵɵlistener("click", function ConfirmSessionComponent_Conditional_12_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.requestLogout()); });
    i0.ɵɵtext(1, " Log Out ");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "button", 13);
    i0.ɵɵlistener("click", function ConfirmSessionComponent_Conditional_12_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.continueSession()); });
    i0.ɵɵtext(3, " Stay Logged In ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(2);
    i0.ɵɵstyleProp("margin-left", "var(--space--16)");
} }
function ConfirmSessionComponent_Conditional_14_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 14);
    i0.ɵɵlistener("click", function ConfirmSessionComponent_Conditional_14_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.requestLogout()); });
    i0.ɵɵtext(1, " Ok ");
    i0.ɵɵelementEnd();
} }
class ConfirmSessionComponent {
    constructor() {
        this.logout = new EventEmitter();
        this.continue = new EventEmitter();
        this.timer$ = new Observable();
        this._dialogRef = inject(StlrDialogRef);
    }
    continueSession() {
        this.continue.emit();
    }
    requestLogout() {
        this.logout.emit();
    }
    static { this.ɵfac = function ConfirmSessionComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ConfirmSessionComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ConfirmSessionComponent, selectors: [["tc-confirm-session"]], outputs: { logout: "logout", continue: "continue" }, decls: 15, vars: 13, consts: [[1, "tc-session-dialog"], [1, "tc-session-dialog__header"], ["color", "primary"], ["translation", "SessionDialog_Header", 3, "margin"], ["translation", "SessionDialog_Header_LoggedOut", 3, "margin"], [1, "tc-session-dialog__container"], ["translation", "SessionDialog_Info"], ["translation", "SessionDialog_LoggedOut"], [1, "tc-session-dialog__footer"], ["stlr-button", "", "btnType", "basic", "translation", "SessionDialog_LoggedOut_Ok"], ["translation", "SessionDialog_Header"], ["translation", "SessionDialog_Header_LoggedOut"], ["stlr-button", "", "btnType", "basic", "translation", "SessionDialog_Logout", 3, "click"], ["stlr-button", "", "translation", "SessionDialog_StayLoggedIn", 3, "click"], ["stlr-button", "", "btnType", "basic", "translation", "SessionDialog_LoggedOut_Ok", 3, "click"]], template: function ConfirmSessionComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "article", 0)(1, "header", 1)(2, "stlr-icon", 2);
            i0.ɵɵtext(3, "timer");
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(4, ConfirmSessionComponent_Conditional_4_Template, 2, 2, "h3", 3);
            i0.ɵɵpipe(5, "async");
            i0.ɵɵconditionalBranchCreate(6, ConfirmSessionComponent_Conditional_6_Template, 2, 2, "h3", 4);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(7, "section", 5);
            i0.ɵɵconditionalCreate(8, ConfirmSessionComponent_Conditional_8_Template, 2, 0, "div", 6);
            i0.ɵɵpipe(9, "async");
            i0.ɵɵconditionalBranchCreate(10, ConfirmSessionComponent_Conditional_10_Template, 2, 0, "div", 7);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(11, "section", 8);
            i0.ɵɵconditionalCreate(12, ConfirmSessionComponent_Conditional_12_Template, 4, 2);
            i0.ɵɵpipe(13, "async");
            i0.ɵɵconditionalBranchCreate(14, ConfirmSessionComponent_Conditional_14_Template, 2, 0, "button", 9);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵstyleProp("margin-right", "var(--space--8)");
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(i0.ɵɵpipeBind1(5, 7, ctx.timer$) > 0 ? 4 : 6);
            i0.ɵɵadvance(3);
            i0.ɵɵstyleProp("margin", "var(--space--32) 0px");
            i0.ɵɵadvance();
            i0.ɵɵconditional(i0.ɵɵpipeBind1(9, 9, ctx.timer$) > 0 ? 8 : 10);
            i0.ɵɵadvance(4);
            i0.ɵɵconditional(i0.ɵɵpipeBind1(13, 11, ctx.timer$) > 0 ? 12 : 14);
        } }, dependencies: [StlrIconModule, i1.StlrIconComponent, StlrButtonModule, i2.StlrButtonComponent, CommonModule, AsyncPipe], styles: ["[_nghost-%COMP%]{width:100%}  .cdk-global-overlay-wrapper .stlr-dialog-pane{--dialog__margins: .5rem;--dialog__width--max: 1000px;--dialog__width--lrg: 800px;--dialog__width--med: 500px;--dialog__width--sml: 300px;--dialog__bg-color: white;--dialog__padding: 1.5rem;--dialog__height: calc(100vh - 5rem) ;--dialog__scrollbar-width: .5rem;--dialog__scrollbar-bg: var(--p-neutral-100);--dialog__scrollbar-thumb: var(--p-neutral-400);--dialog__scrollbar-thumb--hover: var(--p-neutral-500);--dialog__close-bg-color: rgba(255, 255, 255, .8);--dialog__close-padding: .5rem;--dialog__close-border-radius: 4px;left:var(--dialog__margins);right:var(--dialog__margins);width:100%}@media screen and (min-width:37.5em){  .cdk-global-overlay-wrapper .stlr-dialog-pane.max{max-width:var(--dialog__width--max)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.lrg{max-width:var(--dialog__width--lrg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.med{max-width:var(--dialog__width--med)}  .cdk-global-overlay-wrapper .stlr-dialog-pane.sml{max-width:var(--dialog__width--sml)}}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-dialog-container{width:100%}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-container:empty{display:none}  .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-backdrop{background-color:#000000bf}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container{position:relative;background:var(--dialog__bg-color);z-index:9999;margin:auto;width:100%;overflow:auto;height:fit-content;max-height:var(--dialog__height);box-sizing:border-box}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container>h4,   .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container .outlet,   .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding:var(--dialog__padding)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container h4{padding-bottom:0}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding-top:0}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar{width:var(--dialog__scrollbar-width)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-track{background-color:var(--dialog__scrollbar-bg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb{background:var(--dialog__scrollbar-thumb)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb:hover{background:var(--dialog__scrollbar-thumb--hover)}@media screen and (min-width:37.5em){  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.max{max-width:var(--dialog__width--max)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.lrg{max-width:var(--dialog__width--lrg)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.med{max-width:var(--dialog__width--med)}  .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.sml{max-width:var(--dialog__width--sml)}}  .cdk-global-overlay-wrapper .stlr-dialog-pane .outlet{flex:1 1 auto;overflow-y:auto}  .cdk-global-overlay-wrapper .stlr-dialog-pane .close-icon{appearance:none;border:0;display:flex;position:absolute;right:var(--dialog__margins);top:var(--dialog__margins);background-color:var(--dialog__close-bg-color);border-radius:var(--dialog__close-border-radius);padding:var(--dialog__close-padding);cursor:pointer}.tc-session-dialog[_ngcontent-%COMP%]{width:100%;background-color:#fff;font-family:Roboto,sans-serif}.tc-session-dialog__info[_ngcontent-%COMP%]{display:flex;flex-direction:column;text-align:center;margin-top:5px;margin-bottom:5px}.tc-session-dialog__header[_ngcontent-%COMP%]{display:flex;align-items:center;box-sizing:border-box;margin-bottom:var(--space--24)}.tc-session-dialog__container[_ngcontent-%COMP%]{display:flex;width:100%;box-sizing:border-box}.tc-session-dialog__footer[_ngcontent-%COMP%]{display:flex;justify-content:flex-end;width:100%}.tc-session-dialog__timer-detail[_ngcontent-%COMP%]{display:flex;flex-direction:row;justify-content:center;align-items:center;width:60%}.tc-session-dialog__timer[_ngcontent-%COMP%]{margin-left:5px}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ConfirmSessionComponent, [{
        type: Component,
        args: [{ selector: 'tc-confirm-session', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [StlrIconModule, StlrButtonModule, AsyncPipe, CommonModule], template: "<article class=\"tc-session-dialog\">\n  <header class=\"tc-session-dialog__header\">\n    <stlr-icon [style.margin-right]=\"'var(--space--8)'\" color=\"primary\"\n      >timer</stlr-icon\n      >\n      @if ((timer$ | async)! > 0) {\n        <h3\n          [style.margin]=\"'0px'\"\n          translation=\"SessionDialog_Header\"\n          >\n          Are you still there?\n        </h3>\n      } @else {\n        <h3\n          [style.margin]=\"'0px'\"\n          translation=\"SessionDialog_Header_LoggedOut\"\n          >\n          You have been logged out\n        </h3>\n      }\n    </header>\n\n    <section\n      class=\"tc-session-dialog__container\"\n      [style.margin]=\"'var(--space--32) 0px'\"\n      >\n      @if ((timer$ | async)! > 0) {\n        <div\n          translation=\"SessionDialog_Info\"\n          >\n          Would you like to stay logged in?\n        </div>\n      } @else {\n        <div translation=\"SessionDialog_LoggedOut\">\n          You will need to log in to continue.\n        </div>\n      }\n    </section>\n\n    <section class=\"tc-session-dialog__footer\">\n      @if ((timer$ | async)! > 0) {\n        <button\n          stlr-button\n          btnType=\"basic\"\n          (click)=\"requestLogout()\"\n          translation=\"SessionDialog_Logout\"\n          >\n          Log Out\n        </button>\n        <button\n          stlr-button\n          [style.margin-left]=\"'var(--space--16)'\"\n          (click)=\"continueSession()\"\n          translation=\"SessionDialog_StayLoggedIn\"\n          >\n          Stay Logged In\n        </button>\n      } @else {\n        <button\n          stlr-button\n          btnType=\"basic\"\n          (click)=\"requestLogout()\"\n          translation=\"SessionDialog_LoggedOut_Ok\"\n          >\n          Ok\n        </button>\n      }\n    </section>\n  </article>\n", styles: [":host{width:100%}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane{--dialog__margins: .5rem;--dialog__width--max: 1000px;--dialog__width--lrg: 800px;--dialog__width--med: 500px;--dialog__width--sml: 300px;--dialog__bg-color: white;--dialog__padding: 1.5rem;--dialog__height: calc(100vh - 5rem) ;--dialog__scrollbar-width: .5rem;--dialog__scrollbar-bg: var(--p-neutral-100);--dialog__scrollbar-thumb: var(--p-neutral-400);--dialog__scrollbar-thumb--hover: var(--p-neutral-500);--dialog__close-bg-color: rgba(255, 255, 255, .8);--dialog__close-padding: .5rem;--dialog__close-border-radius: 4px;left:var(--dialog__margins);right:var(--dialog__margins);width:100%}@media screen and (min-width:37.5em){::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.max{max-width:var(--dialog__width--max)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.lrg{max-width:var(--dialog__width--lrg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.med{max-width:var(--dialog__width--med)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane.sml{max-width:var(--dialog__width--sml)}}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-dialog-container{width:100%}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-container:empty{display:none}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .cdk-overlay-backdrop{background-color:#000000bf}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container{position:relative;background:var(--dialog__bg-color);z-index:9999;margin:auto;width:100%;overflow:auto;height:fit-content;max-height:var(--dialog__height);box-sizing:border-box}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container>h4,::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container .outlet,::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding:var(--dialog__padding)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container h4{padding-bottom:0}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container footer{padding-top:0}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar{width:var(--dialog__scrollbar-width)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-track{background-color:var(--dialog__scrollbar-bg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb{background:var(--dialog__scrollbar-thumb)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container::-webkit-scrollbar-thumb:hover{background:var(--dialog__scrollbar-thumb--hover)}@media screen and (min-width:37.5em){::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.max{max-width:var(--dialog__width--max)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.lrg{max-width:var(--dialog__width--lrg)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.med{max-width:var(--dialog__width--med)}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .stlr-dialog-container.sml{max-width:var(--dialog__width--sml)}}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .outlet{flex:1 1 auto;overflow-y:auto}::ng-deep .cdk-global-overlay-wrapper .stlr-dialog-pane .close-icon{appearance:none;border:0;display:flex;position:absolute;right:var(--dialog__margins);top:var(--dialog__margins);background-color:var(--dialog__close-bg-color);border-radius:var(--dialog__close-border-radius);padding:var(--dialog__close-padding);cursor:pointer}.tc-session-dialog{width:100%;background-color:#fff;font-family:Roboto,sans-serif}.tc-session-dialog__info{display:flex;flex-direction:column;text-align:center;margin-top:5px;margin-bottom:5px}.tc-session-dialog__header{display:flex;align-items:center;box-sizing:border-box;margin-bottom:var(--space--24)}.tc-session-dialog__container{display:flex;width:100%;box-sizing:border-box}.tc-session-dialog__footer{display:flex;justify-content:flex-end;width:100%}.tc-session-dialog__timer-detail{display:flex;flex-direction:row;justify-content:center;align-items:center;width:60%}.tc-session-dialog__timer{margin-left:5px}\n"] }]
    }], null, { logout: [{
            type: Output
        }], continue: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(ConfirmSessionComponent, { className: "ConfirmSessionComponent", filePath: "lib/confirm-session/confirm-session.component.ts", lineNumber: 21 }); })();

/**
 * NgModule that provides OAuth authentication services and HTTP interceptor.
 *
 * @remarks
 * This module provides:
 * - `AuthenticationService` for login/logout operations
 * - `TokenService` for token management
 * - `AuthenticationGuard` for route protection
 * - HTTP interceptor that adds Bearer tokens to requests
 * - `ConfirmSessionComponent` for session timeout warnings
 *
 * The module uses the NgModule pattern (legacy Angular style) and is suitable
 * for applications not yet migrated to standalone APIs.
 *
 * @example
 * ```typescript
 * // Basic setup without session dialog
 * import { AuthenticationModule } from '@tc/security-oauth-legacy';
 *
 * @NgModule({
 *   imports: [
 *     AuthenticationModule,
 *     // other imports...
 *   ]
 * })
 * export class AppModule { }
 * ```
 *
 * @example
 * ```typescript
 * // Setup with custom session timeout dialog
 * import { AuthenticationModule } from '@tc/security-oauth-legacy';
 * import { StlrDialogService } from '@tc/interstellar-dialog';
 *
 * @NgModule({
 *   imports: [
 *     AuthenticationModule.withSessionDialog<StlrDialogService>(
 *       dialogService => () => dialogService.open(ConfirmSessionComponent),
 *       StlrDialogService
 *     ),
 *     // other imports...
 *   ]
 * })
 * export class AppModule { }
 * ```
 */
class AuthenticationModule {
    /**
     * Configures a custom session dialog for expiring sessions.
     *
     * @param dialogFactory - Factory function that creates and opens the session dialog.
     *                        Receives the dialog service instance and returns a function
     *                        that opens the dialog and returns a dialog reference.
     * @param dialogDependency - The dialog service class to inject (e.g., StlrDialogService).
     *                           This service will be injected and passed to the dialogFactory.
     *
     * @returns ModuleWithProviders containing the AuthenticationModule with session dialog configuration
     *
     * @remarks
     * Use this method when you want to customize the session expiration warning dialog.
     * The dialog will be shown when the user's session is about to expire (based on
     * `verifySessionActiveMs` configuration).
     *
     * The dialog factory should:
     * 1. Accept a dialog service instance
     * 2. Return a function that opens the dialog
     * 3. The dialog should emit 'logout' or 'continue' events
     *
     * @example
     * ```typescript
     * // Using Interstellar Dialog Service
     * import { StlrDialogService } from '@tc/interstellar-dialog';
     * import { ConfirmSessionComponent } from '@tc/security-oauth-legacy';
     *
     * AuthenticationModule.withSessionDialog<StlrDialogService>(
     *   dialogService => () => dialogService.open(ConfirmSessionComponent),
     *   StlrDialogService
     * )
     * ```
     *
     * @example
     * ```typescript
     * // Using Angular Material Dialog
     * import { MatDialog } from '@angular/material/dialog';
     * import { CustomSessionDialog } from './custom-session-dialog';
     *
     * AuthenticationModule.withSessionDialog<MatDialog>(
     *   dialog => () => dialog.open(CustomSessionDialog),
     *   MatDialog
     * )
     * ```
     */
    static withSessionDialog(dialogFactory, dialogDependency) {
        return {
            ngModule: AuthenticationModule,
            providers: [
                {
                    provide: SESSION_DIALOG_TOKEN$1,
                    useFactory: dialogFactory,
                    deps: [dialogDependency],
                }
            ],
        };
    }
    static { this.ɵfac = function AuthenticationModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AuthenticationModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: AuthenticationModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
            AuthenticationService,
            provideHttpClient(withInterceptors([authInterceptor()]))
        ], imports: [CommonModule, ConfirmSessionComponent] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, ConfirmSessionComponent],
                exports: [ConfirmSessionComponent],
                providers: [
                    AuthenticationService,
                    provideHttpClient(withInterceptors([authInterceptor()]))
                ],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(AuthenticationModule, { imports: [CommonModule, ConfirmSessionComponent], exports: [ConfirmSessionComponent] }); })();

/**
 * Route guard that protects routes requiring authentication.
 *
 * @remarks
 * This guard:
 * 1. Waits for any in-progress token refresh to complete
 * 2. Checks if a valid authentication token exists
 * 3. If no token, redirects to login with return URL
 * 4. If token exists, allows route activation
 *
 * Use this guard on routes that require authenticated users.
 *
 * @example
 * ```typescript
 * // Protect a single route
 * const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     component: DashboardComponent,
 *     canActivate: [AuthenticationGuard]
 *   }
 * ];
 * ```
 *
 * @example
 * ```typescript
 * // Protect multiple child routes
 * const routes: Routes = [
 *   {
 *     path: 'admin',
 *     canActivate: [AuthenticationGuard],
 *     children: [
 *       { path: 'users', component: UsersComponent },
 *       { path: 'settings', component: SettingsComponent }
 *     ]
 *   }
 * ];
 * ```
 */
class AuthenticationGuard {
    constructor() {
        this.authService = inject(AuthenticationService);
        this.tokenService = inject(TokenService);
    }
    /**
     * Determines if a route can be activated based on authentication status.
     *
     * @param route - The activated route snapshot
     * @param state - The router state snapshot containing the target URL
     * @returns Observable<boolean> - true if route can be activated, false otherwise
     *
     * @remarks
     * If the user is not authenticated, this method will:
     * - Initiate the login flow
     * - Pass the attempted URL as the destination after successful login
     * - Return false to prevent route activation
     */
    canActivate(route, state) {
        return this.tokenService.isRefreshingToken$.pipe(first((refreshing) => !refreshing), switchMap(() => this.tokenService.token$.pipe(first())), switchMap((token) => {
            if (!token.exists) {
                this.authService.login(`${window.location.protocol}//${window.location.host}${state.url}`);
                return of(false);
            }
            return of(true);
        }));
    }
    static { this.ɵfac = function AuthenticationGuard_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AuthenticationGuard)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthenticationGuard, factory: AuthenticationGuard.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationGuard, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();

class MockAuthenticationService {
    constructor() {
        this.isAuthenticatedSubject = new ReplaySubject(1);
        this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
        this.isVerifyingSessionSubject = new ReplaySubject(1);
        this.isVerifyingSession$ = this.isVerifyingSessionSubject.asObservable();
        this.logoutStartedSubject = new ReplaySubject(1);
        this.logoutStarted$ = this.logoutStartedSubject.asObservable();
        this.sessionTimeoutTimerSubject = new ReplaySubject(1);
        this.sessionTimeoutTimer$ = this.sessionTimeoutTimerSubject.asObservable();
        this.isAuthenticatedSubject.next(false);
        this.isVerifyingSessionSubject.next(false);
        this.logoutStartedSubject.next(false);
        this.sessionTimeoutTimerSubject.next(0);
    }
    login() {
        this.isAuthenticatedSubject.next(true);
    }
    logout() {
        this.logoutStartedSubject.next(true);
        this.isAuthenticatedSubject.next(false);
    }
    continueSession() {
        this.isVerifyingSessionSubject.next(false);
        this.sessionTimeoutTimerSubject.next(0);
    }
    simulateAuthenticated(isAuthenticated) {
        this.isAuthenticatedSubject.next(isAuthenticated);
    }
    simulateSessionTimeout(timeRemaining) {
        this.isVerifyingSessionSubject.next(true);
        this.sessionTimeoutTimerSubject.next(timeRemaining);
    }
    reset() {
        this.isAuthenticatedSubject.next(false);
        this.isVerifyingSessionSubject.next(false);
        this.logoutStartedSubject.next(false);
        this.sessionTimeoutTimerSubject.next(0);
    }
    static { this.ɵfac = function MockAuthenticationService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MockAuthenticationService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockAuthenticationService, factory: MockAuthenticationService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockAuthenticationService, [{
        type: Injectable
    }], () => [], null); })();

/**
 * Mock implementation of TokenService for testing purposes.
 *
 * @remarks
 * This mock provides the same observables and methods as the real TokenService,
 * making it suitable for unit tests that depend on TokenService.
 *
 * @example
 * ```typescript
 * TestBed.configureTestingModule({
 *   providers: [
 *     { provide: TokenService, useClass: MockTokenService }
 *   ]
 * });
 * ```
 */
class MockTokenService {
    constructor() {
        this.Token = 'TEST_TOKEN';
        this.refreshTokenInvalidSubject = new Subject();
        this.refreshTokenInvalid$ = this.refreshTokenInvalidSubject.asObservable();
        this.tokenSubject = new ReplaySubject(1);
        this.token$ = this.tokenSubject.asObservable();
        this.isRefreshingTokenSubject = new ReplaySubject(1);
        this.isRefreshingToken$ = this.isRefreshingTokenSubject.asObservable();
        // Initialize with default state
        this.isRefreshingTokenSubject.next(false);
    }
    /**
     * Simulates token refresh. In mock, just emits refresh state changes.
     */
    refreshToken() {
        this.isRefreshingTokenSubject.next(true);
        // Simulate async token refresh
        setTimeout(() => {
            this.isRefreshingTokenSubject.next(false);
        }, 0);
    }
    /**
     * Clears the test token.
     */
    clearToken() {
        this.Token = '';
        this.isRefreshingTokenSubject.next(false);
    }
    /**
     * Gets the current test token value.
     */
    getToken() {
        return this.Token;
    }
    /**
     * Sets a new test token value.
     */
    setToken(token) {
        this.Token = token;
    }
    /**
     * Test helper: Simulate token refresh invalid scenario.
     */
    simulateRefreshInvalid() {
        this.refreshTokenInvalidSubject.next();
    }
    /**
     * Test helper: Simulate refreshing state.
     */
    simulateRefreshing(isRefreshing) {
        this.isRefreshingTokenSubject.next(isRefreshing);
    }
    static { this.ɵfac = function MockTokenService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MockTokenService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: MockTokenService, factory: MockTokenService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MockTokenService, [{
        type: Injectable
    }], () => [], null); })();

class SessionDialog {
    constructor() {
        this.componentInstance = new ConfirmSessionComponent();
        this.close = () => { return; };
        this.afterClosed = () => new Observable();
    }
}
const SESSION_DIALOG_TOKEN = new InjectionToken('sessionDialog');

/**
 * Generated bundle index. Do not edit.
 */

export { AuthenticationGuard, AuthenticationModule, AuthenticationService, AuthenticationToken, ConfirmSessionComponent, EmptyToken, MockAuthenticationService, MockTokenService, SessionDialog, TokenService };

